import { ContactBankAccount } from "../interfaces/DTO/crm/contact";

/**
 * The client's bank accounts (Contact.data.bank_accounts). A couple's joint account is kept on both of them with
 * the same id - entered once, on either one; the other one's copy follows it (spouseAccountsAfter).
 */

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const newBankAccount = (isDefault: boolean): ContactBankAccount => ({
  id: newId(),
  bank_code: null,
  branch_number: "",
  account_number: "",
  joint: false,
  joint_with_contact_id: null,
  is_default: isDefault,
});

/** One default account when there are any - the marked one, else the first */
export function withOneDefault(accounts: ContactBankAccount[]): ContactBankAccount[] {
  const index = Math.max(0, accounts.findIndex((a) => a.is_default));
  return accounts.map((a, i) => ({ ...a, is_default: i === index }));
}

/** The account forms use: the default, else the first */
export const defaultAccount = (accounts: ContactBankAccount[]) => accounts.find((a) => a.is_default) ?? accounts[0];

const digits = (value: string | null | undefined, min: number, max: number) =>
  new RegExp(`^\\d{${min},${max}}$`).test((value ?? "").trim());

/** What is missing / wrong in an account (null - it is fine) */
export function accountProblem(a: ContactBankAccount): string | null {
  if (!a.bank_code) return "יש לבחור בנק";
  if (!digits(a.branch_number, 1, 3)) return "מספר סניף - עד 3 ספרות";
  if (!digits(a.account_number, 2, 9)) return "מספר חשבון - 2 עד 9 ספרות";
  return null;
}

export const accountsProblem = (accounts: ContactBankAccount[]) =>
  accounts.map(accountProblem).find((p) => p != null) ?? null;

/**
 * The spouse's accounts after the client's were saved: the client's joint accounts are copied to the spouse (the
 * same id, shared with the client), a joint account the client deleted / made their own is removed from the spouse.
 * The spouse's own accounts and their choice of a default stay. null - nothing changed.
 */
export function spouseAccountsAfter(
  clientAccounts: ContactBankAccount[],
  clientId: number,
  spouseAccounts: ContactBankAccount[],
): ContactBankAccount[] | null {
  const joint = clientAccounts.filter((a) => a.joint);
  const jointIds = new Set(joint.map((a) => a.id));
  // the spouse's copies of the client's joint accounts that are no longer joint / were deleted
  const kept = spouseAccounts.filter((a) => !(a.joint && a.joint_with_contact_id === clientId && !jointIds.has(a.id)));
  const next = [...kept];
  for (const account of joint) {
    const copy: ContactBankAccount = {
      ...account,
      joint: true,
      joint_with_contact_id: clientId,
      is_default: false,
    };
    const at = next.findIndex((a) => a.id === account.id);
    if (at >= 0) next[at] = { ...copy, is_default: next[at].is_default };
    else next.push(copy);
  }
  const result = next.length ? withOneDefault(next) : next;
  return JSON.stringify(result) === JSON.stringify(spouseAccounts) ? null : result;
}
